import { useState } from "react"
import { FileText, Download, Send, Upload } from "lucide-react"
import { Button } from "../components/ui/button"

import { SchoolSidebar } from "../components/ui/school/school-dashboard/school-sidebar"
import { ReportStats } from "../components/ui/school/report-center/report-stats"
import { ReportTable } from "../components/ui/school/report-center/report-table"
import StudentDetailsModal from "../components/ui/school/report-center/student-details-modal"
import { UploadResultsModal } from "../components/ui/school/report-center/upload-results-modal"
import { DeleteConfirmModal } from "../components/ui/school/report-center/delete-confirm-modal"

import { UploadBulkResultsModal } from "../components/ui/school/report-center/upload-bulk-results-modal"
import { initialStudents } from "../lib/students-data"

const ReportCenterPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [students, setStudents] = useState(initialStudents)
  const [selectedIds, setSelectedIds] = useState([])
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false)
  const [uploadStudentId, setUploadStudentId] = useState("")
  const [deleteStudentId, setDeleteStudentId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  // Calculate stats
  const totalReports = students.length
  const reportsSent = Math.floor(totalReports * 0.75) // Mock: 75% sent
  const avgGrade = (students.reduce((sum, s) => sum + Number.parseFloat(s.gpa), 0) / totalReports).toFixed(1)

  // Pagination
  const studentsPerPage = 10
  const totalPages = Math.ceil(totalReports / studentsPerPage)
  const startIndex = (currentPage - 1) * studentsPerPage
  const endIndex = startIndex + studentsPerPage
  const currentStudents = students.slice(startIndex, endIndex)

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(currentStudents.map((s) => s.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]))
  }

  const handleViewDetails = (student) => {
    setSelectedStudent(student)
    setIsDetailsModalOpen(true)
  }

  const handleUpload = (id) => {
    const student = students.find((s) => s.id === id)
    if (student) {
      setUploadStudentId(id)
      setSelectedStudent(student)
      setIsUploadModalOpen(true)
    }
  }

  const handleDeleteClick = (id) => {
    setDeleteStudentId(id)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    setStudents(students.filter((s) => s.id !== deleteStudentId))
    setSelectedIds(selectedIds.filter((id) => id !== deleteStudentId))
    setIsDeleteModalOpen(false)
    setDeleteStudentId("")
  }

  const handleExportReports = () => {
    alert("Exporting reports as CSV... (This would trigger actual download in production)")
  }

  const handleSendToParents = () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one student to send reports.")
      return
    }
    alert(`Sending reports to ${selectedIds.length} parent(s) via email...`)
    setSelectedIds([])
  }

  const handleBulkUpload = (file) => {
    console.log("Bulk uploading file:", file.name)
    // Handle bulk upload logic here
  }

  const studentToDelete = students.find((s) => s.id === deleteStudentId)
  const uploadStudent = students.find((s) => s.id === uploadStudentId)

  return (
    <div className="flex min-h-screen bg-background">
      <SchoolSidebar isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

      {isSidebarOpen && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setIsSidebarOpen(false)} />
      )}

      <main className={`flex-1 transition-all duration-300 ${isSidebarOpen ? "md:ml-64" : ""}`}>

        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div>
              <h1 className="text-3xl font-bold text-secondary mb-2">Report Center</h1>
              <p className="text-muted-foreground">
                Manage student academic reports, upload results, and send reports to parents.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setIsBulkUploadModalOpen(true)}
                variant="outline"
                className="border-secondary text-secondary hover:bg-secondary/10 bg-transparent"
              >
                <Upload className="w-5 h-5 mr-2" />
                Upload Student Result
              </Button>
              <Button
                onClick={handleSendToParents}
                variant="outline"
                disabled={selectedIds.length === 0}
                className="border-primary text-primary hover:bg-primary/10 bg-transparent"
              >
                <Send className="w-5 h-5 mr-2" />
                Send to Parents ({selectedIds.length})
              </Button>
              <Button onClick={handleExportReports} className="bg-secondary hover:bg-secondary/90 text-white">
                <Download className="w-5 h-5 mr-2" />
                Export Reports
              </Button>
            </div>
          </div>

          <ReportStats totalReports={totalReports} reportsSent={reportsSent} avgGrade={avgGrade} />

          <ReportTable
            students={currentStudents}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelect={handleSelect}
            onViewDetails={handleViewDetails}
            onUpload={handleUpload}
            onDelete={handleDeleteClick}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </main>

      <StudentDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false)
          setSelectedStudent(null)
        }}
        student={selectedStudent}
      />

      <UploadResultsModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false)
          setUploadStudentId("")
          setSelectedStudent(null)
        }}
        studentId={uploadStudentId}
        studentName={uploadStudent?.name || ""}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setDeleteStudentId("")
        }}
        onConfirm={handleConfirmDelete}
        studentName={studentToDelete?.name || ""}
      />

      <UploadBulkResultsModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onUpload={handleBulkUpload}
      />
    </div>
  )
}

export default ReportCenterPage